import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { getRequest, postRequest, deleteRequest, patchRequest, fileRequest } from "../helpers/fetchHandler"
import type { Guide } from "../constants/types"

const guideKeys = {
  all: ["guides"] as const,
  byDestination: (destinationId: string | undefined) => ["guides", destinationId] as const
}

export function useGuides(destinationId: string | undefined) {
  return useQuery({
    queryKey: guideKeys.byDestination(destinationId),
    queryFn: () => getRequest({ url: `/api/v2/destinations/${destinationId}/guides`, auth: true }) as Promise<Guide[]>,
    enabled: !!destinationId
  })
}

export function useCreateGuide() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      postRequest({ url: "/api/v1/guides", data, auth: true }) as Promise<Guide>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideKeys.all })
    }
  })
}

export function useUpdateGuide() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      patchRequest({ url: `/api/v1/guides/${id}`, data, auth: true }) as Promise<Guide>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideKeys.all })
    }
  })
}

export function useDeleteGuide() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteRequest({ url: `/api/v1/guides/${id}`, auth: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideKeys.all })
    }
  })
}

type UploadResult = {
  results: {
    success: { id: string; url: string; thumbnail: string }[]
  }
}

export function useAddImageToGuide() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ images, guide }: { images: File[]; guide: Guide }) => {
      const formData = new FormData()
      formData.append("teamId", guide.ownerTeamId)
      formData.append("guideId", guide.id)
      images.forEach(image => {
        formData.append("images", image, image.name)
      })

      const uploadResponse = await (fileRequest({ url: "/api/v1/files/uploadImages", data: formData, auth: true }) as Promise<UploadResult>)
      const imageBlocks = uploadResponse.results.success.map(image => ({
        data: { id: image.id, url: image.url, thumbnail: image.thumbnail },
        type: "image"
      }))

      const updatedGuide = {
        ...guide,
        content: {
          ...guide.content,
          blocks: [...(guide.content.blocks), ...imageBlocks]
        }
      }

      return patchRequest({ url: `/api/v1/guides/${guide.id}`, data: updatedGuide, auth: true }) as Promise<Guide>
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideKeys.all })
    }
  })
}

export function useAddVideoToGuide() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ videos, guide }: { videos: File[]; guide: Guide }) => {
      const formData = new FormData()
      formData.append("teamId", guide.ownerTeamId)
      formData.append("guideId", guide.id)
      videos.forEach(video => {
        formData.append("videos", video, video.name)
      })

      const uploadResponse = await (fileRequest({ url: "/api/v1/files/uploadVideos", data: formData, auth: true }) as Promise<UploadResult>)
      const videoBlocks = uploadResponse.results.success.map(video => ({
        data: { id: video.id, url: video.url, thumbnail: video.thumbnail },
        type: "video"
      }))

      const updatedGuide = {
        ...guide,
        content: {
          ...guide.content,
          blocks: [...(guide.content.blocks), ...videoBlocks]
        }
      }

      return patchRequest({ url: `/api/v1/guides/${guide.id}`, data: updatedGuide, auth: true }) as Promise<Guide>
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideKeys.all })
    }
  })
}
